import { Outlet, useLocation, useNavigate } from 'react-router-dom'
import { useEffect } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { useInstitution } from '@/contexts/InstitutionContext'
import { getSelectedInstitutionId, UNAUTHORIZED_EVENT } from '@/lib/api/client'

const PUBLIC_PATHS = new Set([
  '/login',
  '/cadastro',
  '/recuperar-senha',
  '/redefinir-senha',
  '/',
  '/drftadmin',
])

/** Rotas de admin: sem sessão, redirecionar para /drftadmin em vez de /login. */
const ADMIN_PATHS_PREFIXES = [
  '/home-drftadmin',
  '/dash-gerencial',
  '/custos-operacionais',
  '/checklist',
  '/splits-globais',
  '/termos-politicas',
  '/monitoramento',
]

export function isAdminAuthRedirectPath(pathname: string): boolean {
  return ADMIN_PATHS_PREFIXES.some((prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`))
}

function isPublicLandingPath(pathname: string): boolean {
  return pathname.startsWith('/campanha/') || pathname.startsWith('/raspadinha/')
}

/**
 * Usa a mesma fonte de verdade do client (localStorage) para evitar race em produção:
 * o effect pode rodar antes do contexto ter o institutionId no estado.
 */
function useEffectiveInstitutionId(): string | null {
  const { institutionId: fromContext } = useInstitution()
  const fromStorage = getSelectedInstitutionId()
  return fromContext ?? fromStorage
}

export function AuthWatcherLayout() {
  const { guard, token, isLoading, signOut } = useAuth()
  const institutionId = useEffectiveInstitutionId()
  const location = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    if (isLoading || guard !== 'admin') return
    if (location.pathname !== '/home-drftadmin') return
    const stored = getSelectedInstitutionId()
    const hasInstitution = (institutionId ?? stored) !== null && (institutionId ?? stored) !== ''
    if (!hasInstitution) return
    navigate('/home', { replace: true })
  }, [isLoading, guard, institutionId, location.pathname, navigate])

  useEffect(() => {
    if (isLoading || guard !== 'admin') return
    if (location.pathname !== '/home') return
    const stored = getSelectedInstitutionId()
    const hasInstitution = (institutionId ?? stored) !== null && (institutionId ?? stored) !== ''
    if (hasInstitution) return
    navigate('/home-drftadmin', { replace: true })
  }, [isLoading, guard, institutionId, location.pathname, navigate])

  useEffect(() => {
    const handleUnauthorized = () => {
      if (PUBLIC_PATHS.has(location.pathname) || isPublicLandingPath(location.pathname)) {
        return
      }
      signOut()
      const loginPath = isAdminAuthRedirectPath(location.pathname) ? '/drftadmin' : '/login'
      navigate(loginPath, { replace: true })
    }
    window.addEventListener(UNAUTHORIZED_EVENT, handleUnauthorized)
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, handleUnauthorized)
  }, [location.pathname, navigate, signOut])

  useEffect(() => {
    if (isLoading) return
    if (PUBLIC_PATHS.has(location.pathname) || isPublicLandingPath(location.pathname)) {
      return
    }

    if (!token || !guard) {
      const loginPath = isAdminAuthRedirectPath(location.pathname) ? '/drftadmin' : '/login'
      navigate(loginPath, { replace: true })
    }
  }, [guard, isLoading, location.pathname, navigate, token])

  return <Outlet />
}
